// --- Základní entity s ID ---
export interface SettingItem {
  id: number;
  name: string;
  color: string;
  count?: number;
}

export interface Currency {
  id: number;
  name: string;
  count?: number;
}

// Obecný typ pro komponenty správy, který podporuje položky s barvou i bez ní
export type ManageableItem = SettingItem | Omit<Currency, 'color'>;


// --- Typy pro ukládání do DB ---
// Ukládá se verze s cizími klíči (ID)
export interface StoredReceipt {
  id: string; // Hash
  uploadDate: string; // ISO string
  fileName: string;
  receiptDate?: string;
  receiptTime?: string;
  businessName?: string;
  amount?: number;
  paymentMethodId: number;
  categoryId: number;
  currencyId: number;
  tagIds: number[];
  userId: number;
  jsonData: string;
  fileBlob?: Blob;
  fileUrl?: string;
}

// --- Typ pro uživatele ---
export interface User {
  id: number;
  username: string;
  passwordHash: string;
}

export type SafeUser = Omit<User, 'passwordHash'>;


// --- Typ pro použití v UI ---
// Aplikace pracuje s "obohatenou" verzí, kde jsou ID nahrazena celými objekty
export interface Receipt {
  id: string; 
  uploadDate: string; 
  fileName: string;
  receiptDate?: string; 
  receiptTime?: string; 
  businessName?: string;
  amount?: number;
  currency: Currency;
  paymentMethod: SettingItem;
  category: SettingItem;
  tags: SettingItem[];
  uploadedBy: SafeUser;
  jsonData: string; 
  fileBlob?: Blob;   
  fileUrl?: string;    
}

export interface BulkUpdatePayload {
    receiptDate?: string;
    businessName?: string;
    currency?: Currency;
    paymentMethod?: SettingItem;
    category?: SettingItem;
    tags?: SettingItem[];
}

export type SortDirection = 'asc' | 'desc';
export type SortKey = 'receiptDate' | 'businessName' | 'amount' | 'category' | 'paymentMethod' | 'tags';
export interface SortConfig {
  key: SortKey;
  direction: SortDirection;
}

export interface ProductMapping {
  originalName: string;
  canonicalName: string;
}

export interface ProductGroup {
    canonicalName: string;
    originalNames: string[];
}

// --- Nové typy pro API-driven architekturu ---
export interface FilterGroup {
  include: string[];
  exclude: string[];
}

export interface GetReceiptsOptions {
  page?: number;
  limit?: number;
  searchTerm?: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  sortConfig?: SortConfig | null;

  tags_include?: string[];
  tags_exclude?: string[];
  businessNames_include?: string[];
  businessNames_exclude?: string[];
  categories_include?: string[];
  categories_exclude?: string[];
  paymentMethods_include?: string[];
  paymentMethods_exclude?: string[];
  currencies_include?: string[];
  currencies_exclude?: string[];
}


export interface UploadResult {
    results: {
        fileName: string;
        status: 'success' | 'duplicate' | 'error';
        receipt?: Receipt;
        message?: string;
    }[];
}

// Typy pro agregovaná data z Dashboardu
export interface CurrencyStat {
    name: string;
    totalAmount: number;
    receiptCount: number;
}

export interface ChartDataItem {
    name: string;
    value: number;
    color: string;
}

export interface TimeChartDataItem {
    x: number;
    y: number;
}

export interface DashboardOverviewData {
    expenseData: ChartDataItem[];
    refundData: ChartDataItem[];
}

export interface PriceTrackerProductData {
    storeComparisonData: ChartDataItem[];
    timeEvolutionData: TimeChartDataItem[];
}


// --- Fix: Added preference keys and export settings from missing file ---
export const DATE_RANGE_FILTER_PREF_KEY = 'dateRangeFilter';
export const EXPORT_COLUMNS_PREF_KEY = 'exportColumns';
export const SETTINGS_PAGE_SIZE_PREF_KEY = 'settingsPageSize';
export const PROCESSING_PAGE_SIZE_PREF_KEY = 'processingPageSize';
export const AI_MODEL_PREFERENCE_KEY = 'aiModel';
export const AI_PROMPTS_PREFERENCE_KEY = 'aiPrompts';
export const USER_COLOR_HISTORY_PREF_KEY = 'userColorHistory';
export const USER_PER_ITEM_COLOR_HISTORY_PREF_KEY = 'userPerItemColorHistory';
export const LOG_LEVEL_PREF_KEY = 'app_log_level';
export const API_TIMEOUT_PREF_KEY = 'apiTimeoutSeconds';
export const SCANNER_SETTINGS_PREF_KEY = 'scannerSettings';


export interface ExportColumnSetting {
    key: string;
    label: string;
    isAvailable: boolean;
    isDefault: boolean;
}

export const ALL_EXPORTABLE_COLUMNS: { key: string, label: string }[] = [
    { key: 'receiptDate', label: 'Datum účtenky' },
    { key: 'receiptTime', label: 'Čas účtenky' },
    { key: 'businessName', label: 'Podnik' },
    { key: 'amount', label: 'Částka' },
    { key: 'currency', label: 'Měna' },
    { key: 'category', label: 'Kategorie' },
    { key: 'paymentMethod', label: 'Způsob platby' },
    { key: 'tags', label: 'Štítky' },
    { key: 'jsonData.receiptNumber', label: 'Číslo účtenky' },
    { key: 'jsonData.ico', label: 'IČO' },
    { key: 'jsonData.dic', label: 'DIČ' },
    { key: 'jsonData.businessAddress', label: 'Adresa podniku' },
    { key: 'jsonData.shortDescription', label: 'Stručný popis (AI)' },
    { key: 'jsonData.suggestedFileName', label: 'Navrhovaný název souboru (AI)' },
    { key: 'fileName', label: 'Název souboru' },
    { key: 'uploadDate', label: 'Datum nahrání' },
    { key: 'uploadedBy.username', label: 'Nahrál/a' },
    { key: 'fileUrl', label: 'URL souboru' },
    { key: 'jsonData.storedFileName', label: 'Název souboru na serveru' },
];

export const getDefaultExportColumnSettings = (): ExportColumnSetting[] => {
    const defaultKeys = ['receiptDate', 'businessName', 'amount', 'currency', 'category'];
    return ALL_EXPORTABLE_COLUMNS.map(col => ({
        ...col,
        isAvailable: true,
        isDefault: defaultKeys.includes(col.key),
    }));
};

// --- Fix: Added types for Gemini suggestions ---
export interface ColorSuggestion {
    name: string;
    color: string;
}

export interface MergeSuggestionGroup {
    target: string;
    sources: string[];
}
export interface AiModel {
  name: string;
  displayName: string;
  description: string;
  version: string;
  isDefault?: boolean;
}

export type AiPromptKey = 
    | 'receiptExtractionSystemInstruction'
    | 'receiptExtraction'
    | 'receiptExtractionJsonSchema'
    | 'shortenBusinessName'
    | 'suggestColors'
    | 'suggestMerges'
    | 'groupProducts';

export type AiPrompts = Record<AiPromptKey, string>;

// --- PaperSnap Types ---
export type ColorEffect = "color" | "grayscale" | "bw_otsu" | "bw_adaptive_gaussian" | "bw_adaptive_mean" | "bw_simple";

export const COLOR_EFFECTS: { value: ColorEffect; label: string }[] = [
    { value: 'bw_otsu', label: 'Černobílý (Otsu)' },
    { value: 'color', label: 'Barevný' },
    { value: 'grayscale', label: 'Odstíny šedi' },
    { value: 'bw_adaptive_gaussian', label: 'ČB (Adaptivní Gaus.)' },
    { value: 'bw_adaptive_mean', label: 'ČB (Adaptivní průměr)' },
    { value: 'bw_simple', label: 'ČB (Jednoduchý práh)' },
];